import React, { useMemo } from 'react';

const formatValue = (value, col) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;

  const format = col?.format;
  const digits = Number.isFinite(col?.digits) ? col.digits : undefined;
  if (typeof value === 'number') {
    try {
      if (format === 'currency') {
        return value.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: digits ?? 0,
        });
      }
      if (format === 'percent') {
        const scaled = value <= 1 ? value * 100 : value;
        return `${scaled.toFixed(digits ?? 1)}%`;
      }
      if (format === 'number') {
        return value.toLocaleString('en-US', {
          maximumFractionDigits: digits ?? 1,
        });
      }
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const normalizeColumns = (columns, rows) => {
  if (Array.isArray(columns) && columns.length) return columns;
  if (Array.isArray(rows) && rows.length && typeof rows[0] === 'object' && !Array.isArray(rows[0])) {
    return Object.keys(rows[0]).map((key) => ({ key, label: key }));
  }
  return [];
};

const normalizeRows = (rows, columns) => {
  if (!Array.isArray(rows)) return [];
  if (!Array.isArray(columns) || !columns.length) return rows;
  const colKeys = columns.map((c, idx) => c.key || c.label || `col_${idx}`);
  return rows.map((row) => {
    if (Array.isArray(row)) {
      const mapped = {};
      colKeys.forEach((key, idx) => {
        mapped[key] = row[idx];
      });
      return mapped;
    }
    return row;
  });
};

const AtlasTableViz = ({ payload, variant = 'chat' }) => {
  const config = payload?.config || {};
  const columns = useMemo(() => normalizeColumns(config.columns, config.rows), [config.columns, config.rows]);
  const rows = useMemo(() => normalizeRows(config.rows, columns), [config.rows, columns]);
  const title = config.title;
  const notes = config.notes;
  const insight = config.insight_callout;
  const source = config.source_note;

  const wrapperClass = variant === 'brief' ? 'atlas-brief-viz atlas-brief-viz-table' : 'atlas-viz atlas-viz-table';

  if (!columns.length) {
    return (
      <div className={wrapperClass}>
        <div className="atlas-viz-table-empty">Table data unavailable.</div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      {title ? <div className="atlas-viz-table-title">{title}</div> : null}
      {insight ? <div className="atlas-viz-table-insight">{insight}</div> : null}
      <div className="atlas-viz-table-scroll">
        <table>
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={`${col.key || col.label || idx}`}
                  style={{ textAlign: col.align || 'left', width: col.width }}
                  title={col.hint || ''}
                >
                  {col.label || col.key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ridx) => (
              <tr key={`row_${ridx}`}>
                {columns.map((col, cidx) => (
                  <td key={`cell_${ridx}_${cidx}`} style={{ textAlign: col.align || 'left' }}>
                    {formatValue(row?.[col.key], col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {notes || source ? (
        <div className="atlas-viz-table-notes">
          {Array.isArray(notes) ? notes.join(' ') : notes}
          {source ? ` ${source}` : ''}
        </div>
      ) : null}
    </div>
  );
};

export default AtlasTableViz;
